// The world map: one map with every city transitof.com covers. Zoomed out,
// each city is a marker and nothing live is fetched. Nearer, a city's
// network is drawn (static files, once). Zoomed in on a city, its trains are
// polled from its own server, and only while it's on screen (viewport.ts).
// Everything else (the train canvas, the line list, cards, Follow) is the
// shell every city page runs, fed with the visible cities' networks and
// trains in prefixed ids (namespace.ts). A city's own code (its lines, its
// runtime) is imported the first time it's near or flown to (regions.ts).

import * as Feed from '@tol/core/feed';
import { MINOR } from '@tol/core/minor';
import type { Network } from '@tol/core/types';
import type { CardTrain } from '@tol/ui/card';
import { ZOOM_OFFSET } from '@tol/core/view';
import { el, esc } from '@tol/ui/dom';
import { networkRegions } from '@tol/ui/network-view';
import { createShell } from '@tol/ui/shell';
import { primeRegion } from './startup-prime.ts';
import { LngLatBounds } from 'maplibre-gl';
import type { LngLatBoundsLike, MapLibreEvent } from 'maplibre-gl';
import { gtfsRuntime } from './gtfs-region.ts';
import type { GtfsRuntime } from './gtfs-region.ts';
import type { LondonRuntime } from './london-region.ts';
import { combineExtras, extensionStates } from './extensions.ts';
import type { RegionExtension } from './extensions.ts';
import { createLoadCoordinator } from './load-coordinator.ts';
import { localId, mergeNetworks, regionOf } from './namespace.ts';
import { retainNetworks } from './network-retention.ts';
import { nearestRegion } from './roster.ts';
import { createGpsContext, selectPlaceContext } from './place-context.ts';
import type { ContextPlace } from './place-context.ts';
import type { CountryView } from './country-view.ts';
import type { RosterScope } from './roster.ts';
import { placeOfPath } from '../shared/places.ts';
import { hideDateLine } from './seam.ts';
import { addCredits } from './credits.ts';
import { createMarkers } from './markers.ts';
import { createOverview } from './overview.ts';
import { createPlaces } from './places.ts';
import type { Rect } from './markers.ts';
import { REGIONS, loadRegion, openingView, titleArea } from './regions.ts';
import type { LoadedRegion, Region } from './regions.ts';
import type { RegionDeps, RegionRuntime } from './runtime.ts';
import { POLL_ZOOM, formatHash, parseHash, pollsRegion, regionStates } from './viewport.ts';
import { createRailOwnership, reconcileMappedRail, trainRegionCode } from './rail-ownership.ts';
import { isTrains } from '@tol/gtfs/trains';
import type { Box, RegionState } from './viewport.ts';

// Store keys (the store's group ids) per region: London polls three groups.
const KEYS_PER_REGION = 8;
// Every city, from the Bay Area east to Sydney.
const WORLD: [[number, number], [number, number]] = [
  [-130, -45],
  [160, 62],
];

// A phone can't show them all: MapLibre won't draw the world shorter than
// the screen is tall (zoom 0.72 on a 390x844 phone, 166° across). The Bay
// Area to Budapest fits, and the rest are a pan or a tap in the list away.
const PHONE_WORLD: [[number, number], [number, number]] = [
  [-128, 30],
  [40, 55],
];
// The shell's phone layout (the panel at the bottom).
const isPhone = (): boolean => window.innerWidth <= 680;
const VIEWER_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const byId = new Map<string, Region>(REGIONS.map((r) => [r.id, r]));
// The regions whose code is in (install), and their line groups' lines.
const loaded = new Map<string, LoadedRegion>();
const contextPlaces = new Map<string, ContextPlace>();
const groupLines = new Map<string, readonly string[]>();

const client = Feed.createClient({ base: '' });
const runtimes = new Map<string, GtfsRuntime | LondonRuntime>();
const runtimeOf = (t: CardTrain): RegionRuntime | undefined => runtimes.get(regionOf(t.pred.lineId));
// Cities' own additions (London's National Rail), made once the shell is,
// and the regions whose extension runs (while the region polls).
const extensions: RegionExtension[] = [];
const extending = new Set<string>();
const loadedExtensions = new Set<string>();
const running = (): readonly RegionExtension[] => extensions.filter((e) => extending.has(e.region));

// The path's city starts its code and initial static requests while the
// map starts. Runtimes reuse the primed files and London's route responses.
function primePlace(): void {
  const first = placeOfPath(location.pathname)?.place;
  void primeRegion(first ? byId.get(first.code) : undefined, client.getJSON);
}
primePlace();

// Keep Places reachable above the startup loader until the shell is ready.
const places = createPlaces(byId);
let switzerlandPage = placeOfPath(location.pathname)?.place.code === 'ch';
let openingPlace: CountryView | null = null;
const SWISS_RAIL_LABELS: Readonly<Partial<Record<string, string>>> = {
  'Regional trains': 'regional',
  TER: 'regional',
  'Long-distance trains': 'long-distance',
  'TGV inOui': 'long-distance',
  Ouigo: 'long-distance',
  International: 'international',
  Intercités: 'long-distance',
  'Passenger rail (scheduled where no live source)': 'rail',
  'Passenger rail': 'rail',
};
el('#panel').classList.add('places-starting');
let initialized = false;
// Network loads can update the camera's context while a URL flight waits
// for its opening view. Do not replace that history entry mid-navigation.
let readingUrl = 0;
const shell = await createShell({
  hooks: 'worldLive',
  city: 'world',
  // Each city's, once its code is in (install).
  lines: [],
  // No rows until a city is in view (showRows).
  rosterScope: { key: ':', keep: () => false },
  groups: [],
  accuracyLabel: (name) => (switzerlandPage ? (SWISS_RAIL_LABELS[name] ?? name) : name),
  view: {
    bounds: { phone: WORLD, desktop: WORLD },
    minZoom: 0.5 + ZOOM_OFFSET,
    maxZoom: 17,
    // Not quite ±180: a box a whole world wide makes MapLibre's camera
    // constraint fail (its view matrix doesn't invert) as the map opens.
    maxBounds: [
      [-179.9, -80],
      [179.9, 84],
    ],
  },
  // Each city's, while it's drawn (credits.ts).
  credits: [],
  keepRight: (lineId) => {
    const side = loaded.get(regionOf(lineId))?.keepRight ?? false;
    return typeof side === 'function' ? side(localId(lineId)) : side;
  },
  // The viewer's, at world zoom; the city's in view otherwise (showClock).
  timeZone: VIEWER_ZONE,
  locale: 'en-US',
  // Trains, trams, buses and ferries.
  noun: { one: 'vehicle', many: 'vehicles' },
  dataName: 'The operators’ newest data',
  loading: () => REGIONS.some((r) => runtimes.get(r.id)?.loading() === true),
  client,
  staleAfterS: (key) => {
    const region = REGIONS[Math.floor(key / KEYS_PER_REGION)];
    const config = region ? loaded.get(region.id) : undefined;
    return config?.kind === 'gtfs' ? (config.page.staleAfterS ?? 120) : 180;
  },
  // Alerts are available only while the city is polling.
  status: (id) => runtimes.get(regionOf(id))?.status(id),
  idle: (id) => runtimes.get(regionOf(id))?.idle(id),
  card: {
    tripRows: (t, net) => runtimeOf(t)?.tripRows(t, net) ?? [],
    trainNote: (t) => runtimeOf(t)?.trainNote?.(t) ?? t.pred.platform,
    stationSection: (sid) =>
      sid.startsWith('man:mbus-stop:')
        ? {
            title: 'Bus call times',
            rows: () =>
              '<li>Times estimated from the timetable, not operator arrival predictions. Vehicle positions use GPS.</li>',
          }
        : (running()
            .find((e) => e.region === regionOf(sid))
            ?.stationSection?.(sid) ?? null),
  },
  extras: combineExtras(() => extensions, running),
  onHidden: () => {
    if (initialized) update();
  },
  onVisible: () => {
    if (initialized) update();
  },
  // Showing where the viewer is calls off a city flight still waiting for
  // its code, as the reader moving the map does (flyTo).
  onLocate: () => {
    flight++;
  },
  onLinesSubmitted: (net, bounds) => {
    overview.setSubmitted(networkRegions.get(net) ?? [], bounds);
  },
  extraHooks: {
    regions: () => Object.fromEntries(states),
    polling: () => REGIONS.filter((r) => runtimes.get(r.id)?.polling() === true).map((r) => r.id),
  },
});
const { map } = shell;
// The shell's one flat world (no copies, bounds) is at least as wide as the
// window, which leaves the Bay Area under the panel at world zoom. The world
// map repeats instead; the train canvas's one world (view.ts) is kept by
// wrapping the center after every move (below), and at world zoom there are
// no trains anyway.
map.setMaxBounds(null);
map.setRenderWorldCopies(true);
hideDateLine(map);
// Every place's track, thin, at a country's zoom (overview.ts).
const overview = createOverview(map);

// ---------------------------------------------------------------- regions

// Loaded networks, prefixed. Async completions publish through one queue so
// several cities finishing together cause one camera/UI recomputation.
const networks = new Map<string, Network>();
let states = new Map<string, RegionState>();
let swaps = 0;
const loads = createLoadCoordinator(update);
const gpsContext = createGpsContext();
function setNetwork(id: string, net: Network): void {
  if (networks.get(id) === net) return;
  networks.set(id, net);
  swaps++;
}

const problems = new Map<string, string>();
let problemRegion: string | null = null;
function showProblem(): void {
  const message = problemRegion === null || map.isMoving() ? undefined : problems.get(problemRegion);
  if (message !== undefined && message !== '') shell.showToast(esc(message));
  else shell.hideToast();
}
map.on('movestart', () => {
  shell.hideToast();
});
const railOwnership = createRailOwnership(shell.live);
const deps: RegionDeps = {
  getJSON: client.getJSON,
  getSampledJSON: async (path, signal) => {
    const code = trainRegionCode(path);
    try {
      const sample = await client.getSampledJSON(path, signal);
      if (code !== undefined && signal !== undefined && !signal.aborted) {
        if (isTrains(sample.data)) return { ...sample, data: railOwnership.sample(code, sample.data, shell.now()) };
        railOwnership.unavailable(code);
      }
      return sample;
    } catch (error) {
      if (code !== undefined) railOwnership.unavailable(code);
      throw error;
    }
  },
  store: shell.live,
  now: shell.now,
  onUpdate: () => {
    railOwnership.reconcile(shell.now());
    reconcileMappedRail(shell.live);
    shell.updateArrivalUI();
    summary();
    const b = map.getBounds();
    if (
      gpsContext.update(shell.live.trains.values(), [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], shell.now())
    )
      loads.changed();
    // The place in front has its first trains: what waited for them now.
    if (held && !leadIsBusy(holding?.lead ?? null)) loads.changed();
  },
  onProblem: (id, message) => {
    if (message === null) problems.delete(id);
    else problems.set(id, message);
    showProblem();
  },
  onNetwork: (id, net) => {
    setNetwork(id, net);
    loads.changed();
  },
  isGeometry: shell.isGeometryFile,
};

// ---------------------------------------------------------------- code

// A region's code: its lines and groups for the shell, its runtime, London's
// extension. Imported once the view reaches it (update) or it's flown to
// (goTo), once; a failed import is asked for again, later each time (a
// flight asks at once).
const RETRY_MS = 3000;
const RETRY_CAP_MS = 30_000;
const installs = new Map<string, Promise<LoadedRegion>>();
const failures = new Map<string, { readonly count: number; readonly retryAt: number }>();
const waiting = (r: Region): boolean => Date.now() < (failures.get(r.id)?.retryAt ?? 0);
async function installRegion(r: Region): Promise<LoadedRegion> {
  const region = await loadRegion(r);
  const key = REGIONS.indexOf(r) * KEYS_PER_REGION;
  let runtime: GtfsRuntime | LondonRuntime;
  if (region.kind === 'gtfs') runtime = gtfsRuntime(region, key, deps);
  else {
    // Already imported, by loadRegion.
    const london = await import('./london-region.ts');
    runtime = london.londonRuntime(region, key, deps);
    extensions.push(london.londonExtension(region, shell));
  }
  // Its lines (and National Rail's rows) in the list before its runtime,
  // which is what loads a network with them, runs.
  shell.addLines(region.lines, region.groups, region.accuracyGroups);
  for (const g of region.groups) groupLines.set(g.id, g.lines);
  runtimes.set(r.id, runtime);
  loaded.set(r.id, region);
  contextPlaces.set(r.id, {
    ...titleArea(region),
    name: region.name,
    slug: region.place.slug,
    timeZone: region.timeZone,
    locale: region.locale,
  });
  failures.delete(r.id);
  deps.onProblem(r.id, null);
  loads.changed();
  return region;
}
function install(r: Region): Promise<LoadedRegion> {
  let got = installs.get(r.id);
  if (!got) {
    got = installRegion(r);
    installs.set(r.id, got);
    got.catch((error: unknown) => {
      installs.delete(r.id);
      void codeFailed(r, error);
    });
  }
  return got;
}

// Only a deploy's own files exist (packages/server/src/page.ts): after
// another deploy this page's chunks are gone, and its own script answers
// 409. And a browser can keep an import's failure (Chrome 154 does: asking
// again fails without a request). So when the page's server answers, with
// a 409 or for a second failure, the page is loaded again, at most twice a
// minute (with index.html's version guard). Otherwise, retried later.
async function codeFailed(r: Region, error: unknown): Promise<void> {
  const n = (failures.get(r.id)?.count ?? 0) + 1;
  const delay = Math.min(RETRY_MS * 2 ** (n - 1), RETRY_CAP_MS);
  failures.set(r.id, { count: n, retryAt: Date.now() + delay });
  console.warn(`${r.id}: its code is unavailable`, error);
  deps.onProblem(r.id, `${r.name} is unavailable. Retrying shortly.`);
  const status = await fetch(import.meta.url, { method: 'HEAD', cache: 'no-store' }).then(
    (res) => res.status,
    () => 0,
  );
  if ((status === 409 || (status !== 0 && n > 1)) && reload()) return;
  setTimeout(() => {
    failures.set(r.id, { count: n, retryAt: 0 });
    loads.changed();
  }, delay);
}
function reload(): boolean {
  const now = Date.now();
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem('version-reloads') ?? '[]');
    const recent = Array.isArray(stored)
      ? stored.filter((t): t is number => typeof t === 'number' && now - t < 60_000)
      : [];
    if (recent.length >= 2) return false;
    sessionStorage.setItem('version-reloads', JSON.stringify([...recent, now]));
  } catch {
    // No storage (a private window's quota): no loop guard, so no reload.
    return false;
  }
  location.reload();
  return true;
}

function load(r: Region, runtime: RegionRuntime, bounds: Box, reconcile = false): void {
  if (networks.has(r.id) && !reconcile) return;
  loads.run(
    `network:${r.id}`,
    () => runtime.load(bounds),
    (net) => {
      setNetwork(r.id, net);
    },
    (error) => {
      console.warn(`${r.id}: network unavailable`, error);
    },
  );
}

// A city's buses (runtime.full) are fetched a zoom level before
// they fade in (MINOR.LINE_FROM, in Leaflet's zooms), so they're there when
// they appear. Until then its network has none.
const FULL_ZOOM = MINOR.LINE_FROM - ZOOM_OFFSET - 1;
const MINOR_POLL_ZOOM = MINOR.LINE_FROM - ZOOM_OFFSET;
function loadFull(r: Region, runtime: RegionRuntime, bounds: Box): void {
  loads.run(
    `full:${r.id}`,
    () => runtime.full?.(bounds) ?? null,
    (net) => {
      setNetwork(r.id, net);
    },
    (error) => {
      console.warn(`${r.id}: its buses are unavailable`, error);
    },
  );
}

// Only active networks reach interactions and counts. A bounded recent set
// retains layouts and station sources for revisits; detailed track is active-only.
const drawRetained = retainNetworks(shell.setNetwork, (id) => {
  networks.delete(id);
  runtimes.get(id)?.release?.();
});
let drawn = '';
const showCredits = addCredits(map);
function drawNetworks(): void {
  const shown = REGIONS.flatMap((r) => {
    const region = loaded.get(r.id);
    return region && states.get(r.id) !== 'idle' && networks.has(r.id) ? [region] : [];
  });
  const key = `${shown.map((r) => r.id).join(',')}:${swaps}`;
  if (key === drawn) return;
  drawn = key;
  drawRetained(
    new Map(
      shown.flatMap((r) => {
        const net = networks.get(r.id);
        return net ? [[r.id, net] as const] : [];
      }),
    ),
    networks,
  );
  overview.setDrawn(shown.map((r) => r.id));
  showCredits(shown);
}

// Whether the place in front is still getting its network or, polling, its
// first trains (update holds other downloads back meanwhile).
// At most HOLD_MS for one place (its feed down, its first trains never
// come), then the rest load anyway.
const HOLD_MS = 8000;
let held = false;
let holding: { readonly lead: string; readonly since: number } | null = null;
function leadIsBusy(lead: string | null): boolean {
  if (lead === null) return false;
  if (holding?.lead !== lead) {
    holding = { lead, since: Date.now() };
    setTimeout(() => {
      loads.changed();
    }, HOLD_MS);
  }
  if (Date.now() - holding.since >= HOLD_MS) return false;
  const runtime = runtimes.get(lead);
  if (!runtime || !networks.has(lead)) return true;
  return (states.get(lead) ?? 'idle') === 'poll' && (!runtime.polling() || runtime.loading());
}

// Decide, for the camera now, what each region loads and polls, and start
// and stop polling to match.
function update(): void {
  const b = map.getBounds();
  const camera = { zoom: map.getZoom(), bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()] as const };
  states = regionStates(
    camera,
    REGIONS.map((r) => ({
      id: r.id,
      ...(r.coverage === undefined ? {} : { coverage: r.coverage }),
      extent: r.extent,
      tracks: runtimes.get(r.id)?.tracks() ?? null,
      minorTracks: runtimes.get(r.id)?.minorTracks?.() ?? [],
      minorPollZoom: MINOR_POLL_ZOOM,
      ...(r.loadZoom === undefined ? {} : { loadZoom: r.loadZoom }),
      ...(r.pollZoom === undefined ? {} : { pollZoom: r.pollZoom }),
    })),
    document.hidden,
  );
  // A region's code, once the view reaches it (London's, its National
  // Rail's reach too).
  const wanted = regionStates(
    camera,
    REGIONS.map((r) => ({
      id: r.id,
      ...(r.coverage === undefined ? {} : { coverage: r.coverage }),
      extent: r.reach ?? r.extent,
      tracks: null,
      ...(r.loadZoom === undefined ? {} : { loadZoom: r.loadZoom }),
    })),
  );
  for (const r of REGIONS) if (wanted.get(r.id) !== 'idle' && !waiting(r)) void install(r);
  overview.update(camera.zoom);
  const extraStates = extensionStates(camera, extensions, document.hidden);
  gpsContext.update(shell.live.trains.values(), camera.bounds, shell.now());
  const center = map.getCenter();
  const context = selectPlaceContext(
    { ...camera, center: [center.lng, center.lat] },
    [...contextPlaces.values()],
    openingPlace,
    REGIONS.flatMap((r) => {
      const network = networks.get(r.id);
      const state = states.get(r.id) ?? 'idle';
      return state !== 'idle' && network ? [{ id: r.id, center: r.center, network, live: state === 'poll' }] : [];
    }),
    gpsContext.lines(),
  );
  const { place, title, scope } = context;
  switzerlandPage = place?.id === 'ch';
  let changed = false;
  // The place in front first: until its network is in and, polling, its
  // first trains, nothing else is fetched beside them (its buses' network,
  // other places': Amtrak's 336 kB at New York). On a slow line New York's
  // first trains came at 4.6 s, sharing 2 MB of downloads, not 3.9.
  // The first network is not in the roster yet. Its feed must start while
  // that network downloads, so use the camera's nearest eligible region.
  const loadLead =
    place?.id ??
    nearestRegion(
      camera,
      REGIONS.filter((r) => states.get(r.id) !== 'idle'),
    );
  problemRegion = scope.primary ?? loadLead;
  showProblem();
  held = leadIsBusy(loadLead);
  for (const r of REGIONS) {
    const runtime = runtimes.get(r.id);
    const state = states.get(r.id) ?? 'idle';
    if (!runtime) continue;
    const detailed = camera.zoom >= FULL_ZOOM;
    // Do not replace a partition scope immediately before stopping it. The
    // next active view reconciles it; an idle view owns no network work.
    const reconcile = state !== 'idle' && (runtime.view?.(camera.bounds, detailed) ?? false);
    if (state !== 'idle' && (!held || r.id === loadLead)) load(r, runtime, camera.bounds, reconcile);
    // Not only when polled: a view of bus stops alone doesn't poll until
    // the buses' stops are in the network.
    if (state !== 'idle' && !held && detailed && networks.has(r.id)) loadFull(r, runtime, camera.bounds);
    const shouldPoll = pollsRegion(state, camera.zoom, r.pollZoom ?? POLL_ZOOM, r.id === loadLead && !document.hidden);
    if (shouldPoll && !runtime.polling()) {
      runtime.start();
      changed = true;
    } else if (!shouldPoll && runtime.polling()) {
      railOwnership.unavailable(r.id);
      runtime.stop();
      changed = true;
    }
  }
  for (const e of extensions) {
    const state = extraStates.get(e.region) ?? 'idle';
    if (state !== 'idle' && !loadedExtensions.has(e.region)) {
      loads.run(
        `extension:${e.region}`,
        e.load,
        () => {
          loadedExtensions.add(e.region);
        },
        (error) => {
          console.warn(`${e.region}: extension unavailable`, error);
        },
      );
    }
    const on = state === 'poll';
    if (on && !extending.has(e.region)) {
      extending.add(e.region);
      e.start();
    } else if (!on && extending.has(e.region)) {
      extending.delete(e.region);
      e.stop();
    }
  }
  drawNetworks();
  showClock(place);
  const heading = el('h1');
  if (heading.textContent !== title) heading.textContent = title;
  if (document.title !== title) document.title = title;
  if (changed) shell.updateArrivalUI();
  showRows(scope);
  summary();
  if (initialized && readingUrl === 0 && !map.isMoving()) syncUrl(context.path);
}

let clockOf: string | null = null;
function showClock(r: ContextPlace | undefined): void {
  const key = r?.id ?? '';
  if (key === clockOf) return;
  clockOf = key;
  if (r) shell.setTimeZone(r.timeZone, r.locale, r.name);
  else shell.setTimeZone(VIEWER_ZONE, navigator.language, 'Your time');
}

// The line list shows the nearest city's roster. A larger network that also
// reaches the view contributes its nearby lines, not its distant city lines.
// The shell makes rows for only these (with every city's line in one list,
// about 10,000 rows at world zoom, most of them hidden).
function keepFor(scope: RosterScope): (id: string) => boolean {
  return (id) =>
    scope.primaryLines.has(id) ||
    scope.nearby.has(id) ||
    groupLines.get(id)?.some((line) => scope.primaryLines.has(line) || scope.nearby.has(line)) === true;
}
function showRows(scope: RosterScope): void {
  const keep = keepFor(scope);
  shell.setRosterScope({
    key: `${switzerlandPage}:${scope.primary ?? ''}:${[...scope.primaryLines].join(',')}:${[...scope.nearby].join(',')}`,
    keep: switzerlandPage ? (id) => regionOf(id) === 'ch' || keep(id) : keep,
    ...(scope.primary === null ? {} : { first: (id: string) => regionOf(id) === scope.primary }),
  });
  const head = el('.roster-head');
  const display = scope.primary === null ? 'none' : '';
  if (head.style.display !== display) head.style.display = display;
  // No lines on screen, no lines button (and no open, empty list).
  const toggle = el('#panel-toggle');
  if (scope.primary === null && !el('#panel').classList.contains('collapsed')) toggle.click();
  toggle.hidden = scope.primary === null;
}

// Zoomed out, the summary says what's on the map instead of "0 trains".
// An extension polling alone (National Rail beyond TfL) has trains to count.
function summary(): void {
  const polling = REGIONS.filter((r) => runtimes.get(r.id)?.polling() === true);
  if (polling.length > 0 || extending.size > 0) return;
  const node = el('#summary');
  const text =
    map.getZoom() < POLL_ZOOM
      ? `${REGIONS.length} cities and regions with live transit. Zoom in on one to see it move.`
      : 'No live transit here yet.';
  if (node.textContent !== text) node.textContent = text;
  const updated = el('#updated');
  if (updated.textContent !== '') updated.textContent = '';
  updated.title = '';
  el('.live-dot').classList.remove('stale');
}

// ---------------------------------------------------------------- markers

// Room for the panel in a fit, as the shell opens a city: beside it on a
// laptop, above it on a phone.
function panelPadding(): { top: number; bottom: number; left: number; right: number } {
  const panel = el('#panel').getBoundingClientRect();
  return isPhone()
    ? { top: 0, left: 0, right: 0, bottom: Math.max(0, window.innerHeight - panel.top) }
    : { top: 0, bottom: 0, right: 0, left: panel.right };
}

// A region's opening view is its page's, in its code: a flight waits for it
// (the code it lands on anyway), and flies to the region's extent without.
// A newer flight, or the reader moving the map meanwhile, calls it off.
let flight = 0;
map.on('movestart', (e: MapLibreEvent<MouseEvent | TouchEvent | WheelEvent | undefined>) => {
  if (e.originalEvent) flight++;
});
function openingOf(r: Region): Promise<LngLatBoundsLike> {
  const [w, s, e, n] = r.extent;
  return install(r).then(
    (region) => openingView(region, isPhone()),
    (): LngLatBoundsLike => [
      [w, s],
      [e, n],
    ],
  );
}
async function flyTo(rs: readonly Region[], fly: (view: Box) => void): Promise<void> {
  const mine = ++flight;
  const views = await Promise.all(rs.map((r) => openingOf(r)));
  if (mine !== flight) return;
  const [first, ...more] = views;
  if (first === undefined) return;
  const box = LngLatBounds.convert(first);
  for (const v of more) box.extend(LngLatBounds.convert(v));
  fly([box.getWest(), box.getSouth(), box.getEast(), box.getNorth()]);
}

// No closer than this when opening a city: its page's view is the heart of
// it (Midtown, downtown San Francisco), a city's own page's opening; on the
// world map a city opens with some of its surroundings. MapLibre's zoom:
// every city polls from POLL_ZOOM (9), so either is well inside.
const openingZoom = (): number => (isPhone() ? 11.5 : 12);

function rememberPlace(
  r: Region,
  landing:
    | {
        readonly center?:
          | readonly [number, number]
          | { readonly lng: number; readonly lat: number }
          | { readonly lon: number; readonly lat: number };
        readonly zoom?: number;
      }
    | undefined,
): void {
  if (!landing) return;
  const center = landing.center;
  if (!center || landing.zoom === undefined) return;
  const point =
    'lat' in center
      ? { lon: 'lng' in center ? center.lng : center.lon, lat: center.lat }
      : { lon: center[0], lat: center[1] };
  openingPlace = { id: r.id, ...point, zoom: landing.zoom };
}

function goTo(r: Region): void {
  switzerlandPage = r.id === 'ch';
  openingPlace = null;
  void flyTo([r], (view) => {
    // Places just closed. Match its new padding before fitting, so the
    // panel's ResizeObserver cannot stop the flight with a padding-only jumpTo.
    // Bounds fitting adds its options' padding to this transform padding.
    map.setPadding(panelPadding());
    const options = {
      animate: !shell.reduceMotion,
      maxZoom: openingZoom(),
      essential: true,
    };
    // The flight lands where the city is polled: its server can start its
    // refresh now instead of when the flight ends (a request it would get a
    // second later anyway).
    const landing = map.cameraForBounds([...view], options);
    rememberPlace(r, landing);
    const runtime = runtimes.get(r.id);
    if (landing?.zoom !== undefined && landing.zoom >= POLL_ZOOM && runtime?.polling() === false)
      runtime.prefetch?.(view, landing.zoom >= FULL_ZOOM);
    map.fitBounds([...view], options);
  });
}

// The part of the map the panel doesn't cover: beside it on a laptop, above
// it on a phone.
function clearArea(): Rect {
  const panel = el('#panel').getBoundingClientRect();
  const { clientWidth: w, clientHeight: h } = map.getContainer();
  return isPhone() ? [0, 0, w, Math.min(h, panel.top)] : [Math.min(w, panel.right), 0, w, h];
}

createMarkers(map, REGIONS, {
  hideZoom: POLL_ZOOM,
  clear: clearArea,
  go: (ids) => {
    const rs = ids.flatMap((id) => byId.get(id) ?? []);
    const [first] = rs;
    if (first === undefined) return;
    if (rs.length === 1) {
      goTo(first);
      return;
    }
    // Cities drawn as one dot: all of them.
    switzerlandPage = false;
    openingPlace = null;
    void flyTo(rs, (box) => {
      const pad = panelPadding();
      map.fitBounds([...box], {
        animate: !shell.reduceMotion,
        padding: { top: pad.top + 40, bottom: pad.bottom + 40, left: pad.left + 40, right: pad.right + 40 },
        essential: true,
      });
    });
  },
});

// The lines button, hidden with no lines on screen (scope()), on every
// screen: the shell's CSS gives it a display the hidden attribute loses to.
{
  const style = document.createElement('style');
  style.textContent = '#panel-toggle[hidden] { display: none; }';
  document.head.append(style);
}

// ---------------------------------------------------------------- the URL

// transitof.com/nyc opens on New York's own opening view; #zoom/lat/lon (or
// #nyc) on any view. While live track is in view, the path follows it, so a copied
// URL says where you were: transitof.com/nyc#11.2/40.71/-74.01.
// The page opens once a city's view is (the loader covers its code coming).
async function fromUrl(): Promise<void> {
  // A coordinate hash also cancels a previous place's pending opening view.
  flight++;
  const mine = flight;
  readingUrl++;
  try {
    const h = parseHash(location.hash);
    const place = placeOfPath(location.pathname)?.place;
    switzerlandPage = h?.kind === 'region' ? h.id === 'ch' : place?.code === 'ch';
    const r = h?.kind === 'region' ? byId.get(h.id) : place ? byId.get(place.code) : undefined;
    openingPlace = null;
    const options = { animate: false, maxZoom: openingZoom(), padding: panelPadding() };
    if (h?.kind === 'at') {
      map.jumpTo({ center: [h.lon, h.lat], zoom: h.zoom });
      if (r) {
        const view = await openingOf(r);
        if (mine === flight) rememberPlace(r, map.cameraForBounds(view, options));
      }
    } else if (r) {
      await flyTo([r], (view) => {
        rememberPlace(r, map.cameraForBounds([...view], options));
        map.fitBounds([...view], options);
      });
    } else map.fitBounds(isPhone() ? PHONE_WORLD : WORLD, { animate: false, padding: panelPadding() });
  } finally {
    readingUrl--;
    if (initialized && readingUrl === 0) update();
  }
}
window.addEventListener('hashchange', () => {
  void fromUrl();
});
// Path-only history entries do not dispatch hashchange.
window.addEventListener('popstate', () => {
  void fromUrl();
});
function syncUrl(path: string): void {
  const c = map.getCenter();
  // Relative, so the page works under a path too: "nyc" or ".".
  const url = new URL(path + formatHash(map.getZoom(), c.lat, c.lng), location.href);
  if (url.href !== location.href) history.replaceState(null, '', url.href);
}
map.on('moveend', () => {
  const c = map.getCenter();
  if (Math.abs(c.lng) > 180) {
    // jumpTo fires moveend again, with the wrapped center.
    map.jumpTo({ center: c.wrap() });
    return;
  }
  update();
});

// Listen before awaiting the opening view, so navigation during its load
// can cancel it. An early Places selection takes precedence over the URL.
if (!places.connect(goTo)) await fromUrl();

// No network yet: an empty one, so the shell's loop runs.
shell.setNetwork(mergeNetworks([]));
shell.ready();
el('#panel').classList.remove('places-starting');
initialized = true;
update();
